import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { Phase } from "@offsite/shared";
import { api } from "@/api/client";
import {
  READ_ONLY_TITLE,
  useReadOnly,
  useStore,
  usePhase,
  useWorkshop,
} from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import { useSitzungsZahlen } from "@/hooks/useSitzungsZahlen";
import { Timer } from "./primitives/Timer";

/**
 * Moderationsleiste — der Ablauf der Sitzung, unten in der Inhaltsfläche.
 *
 * Vorher stand das alles in der Seitenleiste. Falsch für diesen Aufbau: Der
 * Moderator fährt einen Bildschirm und klappt die Navigation zum Vortrag ein.
 * Was er während der 25 Minuten braucht, muss deshalb neben der Navigation
 * liegen, nicht in ihr.
 *
 * In der Seitenleiste bleibt, was nicht zum Vortragsfluss gehört: Rahmenthema
 * (Vorbereitung), Reserve (Notfall, soll unauffällig bleiben) und der
 * Gruppenwechsel.
 *
 * Die Leiste ist bewusst nüchtern gehalten — sie steht auf demselben Bild wie
 * die Folie, das Gremium sieht sie mit. Sie liest sich als Fortschrittsanzeige
 * mit einem Knopf, nicht als Schaltpult.
 */

/**
 * HTTP-Status eines fehlgeschlagenen Aufrufs — ohne `instanceof`.
 *
 * Der API-Client wirft `ApiError` mit `status`. Geprüft wird hier trotzdem am
 * Feld: In den Tests ist das Client-Modul ersetzt, und ein `instanceof` gegen
 * eine dort nicht vorhandene Klasse würde die Fehlerbehandlung selbst zum
 * Fehler machen.
 */
function status(e: unknown): number | undefined {
  return typeof e === "object" && e !== null && "status" in e
    ? (e as { status?: number }).status
    : undefined;
}

/** Arbeitsphase laut Ablaufplan: Minute 5 bis 13. */
const WORKING_DURATION_SEC = 8 * 60;

interface AblaufSchritt {
  id: string;
  label: string;
  /** Phase, in der dieser Schritt als „aktiv“ gilt. */
  phase: Phase;
  /** Statt `phase.set`: den Generierungs-Endpunkt rufen. */
  trigger?: "overnight";
  /** Nur die Leinwand umblättern, keine Phase setzen. */
  nurLeinwand?: boolean;
  /** Startet die acht Minuten. */
  timerStart?: boolean;
  /** Wohin die Leinwand nach diesem Schritt schaut. */
  leinwand: "waiting" | "thema" | "working" | "overnight" | "overlay";
  /**
   * Nach diesem Schritt folgt der Exkurs — was er ist und wohin er führt, steht
   * in der Offsite-Config (`exkurs`). Zwischen „Erzeuge Spikes" und „Ergebnisse
   * zeigen" wechselt der Moderator in die Plattform, die KI baut derweil weiter.
   * Ohne Marker sähe die Kette aus, als folgte das eine unmittelbar dem anderen.
   */
  exkursDanach?: boolean;
}

const SCHRITTE: AblaufSchritt[] = [
  { id: "warten", label: "Warten auf Gäste", phase: "waiting", leinwand: "waiting" },
  {
    id: "thema",
    label: "Thema",
    // Kein Phasenwechsel: die Runde wählt, bevor das Prompten aufgeht, und der
    // Warte-Screen bleibt, über den Nachzügler hereinkommen.
    phase: "waiting",
    nurLeinwand: true,
    leinwand: "thema",
  },
  {
    id: "prompten",
    label: "Prompten öffnen",
    phase: "working",
    // Kein Timer: Das Formular geht auf, damit alle hineinfinden und anfangen
    // können. Die Uhr startet erst im nächsten Schritt — acht Minuten, die
    // während des Hineinfindens ablaufen, sind verschenkt.
    // Die Leinwand behält Adresse, PIN und QR-Code, damit Nachzügler noch
    // hereinkommen.
    leinwand: "waiting",
  },
  {
    id: "arbeitsphase",
    label: "Arbeitsphase",
    phase: "working",
    timerStart: true,
    leinwand: "working",
  },
  {
    id: "spikes",
    label: "Erzeuge Spikes",
    phase: "overnight",
    trigger: "overnight",
    leinwand: "overnight",
    exkursDanach: true,
  },
  { id: "ergebnisse", label: "Ergebnisse zeigen", phase: "reveal", leinwand: "overlay" },
];

const PHASE_KLARTEXT: Partial<Record<Phase, string>> = {
  waiting: "Warten auf Gäste",
  working: "Prompten ist offen",
  overnight: "Generierung läuft",
  reveal: "Ergebnisse auf der Leinwand",
};

function mmss(sekunden: number): string {
  const s = Math.max(0, Math.floor(sekunden));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

interface Props {
  /** Folie, die gerade steht — „Thema" erkennt sich daran. */
  slideIndex: number;
  onJumpSlide: (index: number) => void;
  onToggleForceSlides: (next: boolean) => void;
}

export function ArModerationsleiste({
  slideIndex,
  onJumpSlide,
  onToggleForceSlides,
}: Props) {
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const config = useOffsiteConfig();
  const phase = usePhase();
  const readOnly = useReadOnly();
  const { timerEndsAt, timerPausedRemainingSec, timerBeendetAt } =
    useWorkshop().phase;

  const timerRunning = typeof timerEndsAt === "number" && timerEndsAt > Date.now();
  const timerPausiert =
    !timerRunning &&
    typeof timerPausedRemainingSec === "number" &&
    timerPausedRemainingSec > 0;

  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  /**
   * Eigene Höhe an das Layout melden.
   *
   * Die Leiste ist nicht immer gleich hoch: Timer-Knöpfe kommen und gehen, und
   * auf schmalen Fenstern bricht sie in zwei Zeilen. Eine fest verdrahtete Höhe
   * im CSS war deshalb zweimal falsch — die Leiste legte sich über den Fuß der
   * Folie. Die Folienfläche liest den gemessenen Wert.
   */
  const leisteRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = leisteRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const melden = () => {
      document.documentElement.style.setProperty(
        "--ar-leiste-h",
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    };
    melden();
    const beobachter = new ResizeObserver(melden);
    beobachter.observe(el);
    return () => {
      beobachter.disconnect();
      document.documentElement.style.removeProperty("--ar-leiste-h");
    };
  }, []);

  const zahlen = useSitzungsZahlen(
    phase === "waiting" || phase === "working" || phase === "overnight",
    token,
  );
  const overnight = useOvernightStatus(phase === "overnight" || phase === "reveal");

  /**
   * Leinwand auf das Ziel eines Schritts bringen — und dort halten.
   *
   * Die Folien-Sperre wird gesetzt, damit ein späterer Phasenwechsel den Sprung
   * nicht überschreibt. Für "overlay" wird sie gelöst: nur dann darf die
   * Workshop-Oberfläche die Leinwand übernehmen.
   */
  const leinwandAuf = useCallback(
    (ziel: AblaufSchritt["leinwand"]) => {
      if (ziel === "overlay") {
        onToggleForceSlides(false);
        return;
      }
      const index =
        ziel === "thema"
          ? config.themaSlide
          : ziel === "working"
            ? (config.workingSlide ?? config.waitingSlide)
            : ziel === "overnight"
              ? config.overnightAnnouncementSlide
              : config.waitingSlide;
      if (index === undefined) return;
      onToggleForceSlides(true);
      onJumpSlide(index);
    },
    [
      onJumpSlide,
      onToggleForceSlides,
      config.themaSlide,
      config.workingSlide,
      config.overnightAnnouncementSlide,
      config.waitingSlide,
    ],
  );

  /** Prompten öffnen, ohne die Uhr zu starten — wie „Thema nutzen" auf der Folie. */
  const promptenOeffnen = useCallback(async () => {
    setBusy(true);
    setFehler(null);
    try {
      const p = await api.phase.set(token, "working");
      dispatch({ type: "phase/set", phase: p });
      leinwandAuf("waiting");
    } catch (e) {
      console.error(e);
      setFehler("Prompten konnte nicht geöffnet werden.");
    } finally {
      setBusy(false);
    }
  }, [token, dispatch, leinwandAuf]);

  const startTimer = useCallback(async () => {
    setBusy(true);
    setFehler(null);
    try {
      const p = await api.phase.set(token, "working", WORKING_DURATION_SEC);
      dispatch({ type: "phase/set", phase: p });
      // Erst mit der Uhr wechselt die Leinwand auf die Arbeitsphasen-Folie.
      // „Prompten öffnen" lässt QR-Code und PIN stehen, damit Nachzügler
      // noch hereinfinden.
      leinwandAuf("working");
    } catch (e) {
      console.error(e);
      setFehler("Timer konnte nicht gestartet werden.");
    } finally {
      setBusy(false);
    }
  }, [token, dispatch, leinwandAuf]);

  /**
   * Einen Schritt zurück in „Prompten offen": Uhr weg und Beendet-Stempel weg.
   *
   * Zwei Aufrufe, weil das Backend beides getrennt führt: `resetTimer` räumt
   * die Uhr ab und setzt dabei den Stempel, `phase.set("working")` schreibt die
   * Phasenzeile ohne Timerfelder neu und löscht ihn damit wieder.
   */
  const zurueckZuPrompten = useCallback(async () => {
    setBusy(true);
    setFehler(null);
    try {
      await api.admin.resetTimer(token);
      const p = await api.phase.set(token, "working");
      dispatch({ type: "phase/set", phase: p });
      leinwandAuf("waiting");
    } catch (e) {
      console.error(e);
      setFehler("Zurück zu „Prompten offen“ fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }, [token, dispatch, leinwandAuf]);

  const schrittAusfuehren = useCallback(
    async (schritt: AblaufSchritt) => {
      if (schritt.nurLeinwand) {
        setFehler(null);
        leinwandAuf(schritt.leinwand);
        return;
      }
      if (schritt.timerStart) {
        await startTimer();
        return;
      }
      setBusy(true);
      setFehler(null);
      try {
        if (schritt.trigger === "overnight") {
          // Das Backend setzt die Phase im Trigger selbst — ein zweites
          // phase.set würde der Lambda-Zustandsmaschine dazwischenfunken.
          //
          // 409 heißt: die Generierung läuft bereits (Doppelklick, zweiter
          // Bildschirm, Reload). Das ist kein Fehlschlag — vorher stand
          // deshalb „Erzeuge Spikes fehlgeschlagen" auf dem Pult, während die
          // Prototypen längst gebaut wurden, und die Leinwand blieb stehen.
          try {
            await api.overnight.trigger(token);
          } catch (e) {
            if (status(e) !== 409) throw e;
          }
          const p = await api.phase.get(token);
          dispatch({ type: "phase/set", phase: p });
        } else {
          const p = await api.phase.set(token, schritt.phase);
          dispatch({ type: "phase/set", phase: p });
        }
        leinwandAuf(schritt.leinwand);
      } catch (e) {
        console.error(e);
        setFehler(`„${schritt.label}“ fehlgeschlagen.`);
      } finally {
        setBusy(false);
      }
    },
    [token, dispatch, leinwandAuf, startTimer],
  );

  const timerAktion = useCallback(
    async (aktion: "pause" | "resume" | "plus" | "stop") => {
      setBusy(true);
      setFehler(null);
      try {
        // Die Timer-Endpunkte antworten mit { phase }, nicht mit der Phase selbst.
        const r =
          aktion === "pause"
            ? await api.admin.pauseTimer(token)
            : aktion === "resume"
              ? await api.admin.resumeTimer(token)
              : aktion === "plus"
                ? await api.admin.setTimer(token, 120, "extend")
                : await api.admin.resetTimer(token);
        dispatch({ type: "phase/set", phase: r.phase });
      } catch (e) {
        console.error(e);
        setFehler(
          aktion === "stop"
            ? "Beenden fehlgeschlagen — die Uhr läuft weiter."
            : "Timer-Änderung fehlgeschlagen.",
        );
      } finally {
        setBusy(false);
      }
    },
    [token, dispatch],
  );

  /**
   * Bei welchem Schritt stehen wir? Abgeleitet, nicht gespeichert: Phase und
   * Leinwand sagen es zusammen. „Thema" hat keine eigene Phase und erkennt sich
   * an der Folie. Ein gespeicherter Zeiger wäre nach jedem Reload falsch.
   */
  const aufThemenFolie =
    config.themaSlide !== undefined && slideIndex === config.themaSlide;
  const aktuelleId = aufThemenFolie
    ? "thema"
    : phase === "working"
      ? // Drei Zustände in einer Phase, unterschieden über die Uhr:
        // läuft/pausiert → Arbeitsphase; beendet (Stempel) → Arbeitsphase ist
        // vorbei, aber abgeschlossen, nicht zurückgenommen; gar keine Uhr →
        // das Formular ist offen, die Zeit hat noch nicht angefangen.
        timerRunning || timerPausiert || typeof timerBeendetAt === "number"
        ? "arbeitsphase"
        : "prompten"
      : phase === "overnight"
        ? "spikes"
        : phase === "reveal"
          ? "ergebnisse"
          : "warten";
  const aktuellerIndex = SCHRITTE.findIndex((s) => s.id === aktuelleId);

  /**
   * Was der Weiter-Knopf tut. Die Arbeitsphase hat zwei Stufen — erst die Uhr,
   * dann weiter; sonst käme man mit einem Klick an der Zeit vorbei, um die es in
   * dem Schritt gerade geht.
   */
  /**
   * Ein Schritt zurück. Nicht nur Blättern: die Phase muss mit, sonst stünde die
   * Leinwand woanders als die Handys. Zurück aus der Arbeitsphase heißt deshalb
   * „Prompten wieder zu" — der Warte-Screen kommt zurück, die Uhr bleibt stehen.
   */
  const zurueck = (():
    | { label: string; run: () => void }
    | null => {
    switch (aktuelleId) {
      case "thema":
        return {
          label: "Warten auf Gäste",
          run: () => void schrittAusfuehren(SCHRITTE[0]),
        };
      case "prompten":
        return { label: "Thema", run: () => void schrittAusfuehren(SCHRITTE[1]) };
      case "arbeitsphase":
        // Zurück heißt hier: Uhr weg, Formular bleibt offen. Niemand verliert
        // seinen Text, und der Timer lässt sich neu starten. Anders als
        // „Beenden" nimmt das den Schritt wirklich zurück — deshalb fällt
        // dabei auch der Beendet-Stempel weg.
        return {
          label: "Prompten ohne Uhr",
          run: () => void zurueckZuPrompten(),
        };
      case "spikes":
        // Kein Zurück in die Arbeitsphase: die Generierung läuft schon, und ein
        // Phasenwechsel würde der Lambda dazwischenfunken.
        return null;
      case "ergebnisse":
        return null;
      default:
        return null;
    }
  })();

  const weiter = (():
    | { label: string; run: () => void }
    | null => {
    switch (aktuelleId) {
      case "warten":
        return { label: "Thema", run: () => void schrittAusfuehren(SCHRITTE[1]) };
      case "thema":
        return { label: "Prompten öffnen", run: () => void promptenOeffnen() };
      case "prompten":
        return { label: "Timer starten (8 Min)", run: () => void startTimer() };
      case "arbeitsphase":
        return {
          label: "Erzeuge Spikes",
          run: () => void schrittAusfuehren(SCHRITTE[4]),
        };
      case "spikes":
        return {
          label: "Ergebnisse zeigen",
          run: () => void schrittAusfuehren(SCHRITTE[5]),
        };
      default:
        return null;
    }
  })();

  /** Was rechts neben dem Schrittnamen steht — die Zahl, auf die es ankommt. */
  const lage = (() => {
    if (phase === "overnight") {
      if (!overnight) return "Fortschritt wird geladen…";
      const { done, total, status } = overnight;
      return status === "complete" || (total > 0 && done >= total)
        ? `${done} Prototypen fertig`
        : `${done} von ${total || "…"} fertig`;
    }
    if (phase === "reveal" && overnight && overnight.done > 0) {
      return `${overnight.done} Prototypen auf der Leinwand`;
    }
    if (!zahlen) return PHASE_KLARTEXT[phase] ?? phase;
    return phase === "working"
      ? `${zahlen.eingereicht} von ${zahlen.angemeldet} eingereicht`
      : `${zahlen.angemeldet} ${zahlen.angemeldet === 1 ? "Gast" : "Gäste"} angemeldet`;
  })();

  if (readOnly) return null;

  return (
    <div className="ar-leiste" data-testid="ar-leiste" ref={leisteRef}>
      <div className="ar-leiste-schritte" role="group" aria-label="Ablauf der Sitzung">
        {SCHRITTE.map((schritt, idx) => (
          <Fragment key={schritt.id}>
            <button
              type="button"
              className={`ar-leiste-schritt${idx === aktuellerIndex ? " is-aktiv" : ""}${
                idx < aktuellerIndex ? " is-erledigt" : ""
              }`}
              data-testid={`ar-leiste-${schritt.id}`}
              onClick={() => void schrittAusfuehren(schritt)}
              disabled={busy}
              title={`Direkt zu: ${schritt.label}`}
            >
              <span className="ar-leiste-num">{idx + 1}</span>
              <span className="ar-leiste-label">{schritt.label}</span>
            </button>

            {/* Der Exkurs steht in der Kette, obwohl er nicht in der App
             *  passiert: Sonst sähe es aus, als folgte „Ergebnisse zeigen"
             *  unmittelbar auf „Erzeuge Spikes" — und der Moderator hätte keinen
             *  Anhalt, dass hier der Wechsel ins SVO OS vorgesehen ist. */}
            {schritt.exkursDanach && config.exkurs && (
              <a
                className={`ar-leiste-exkurs${
                  idx === aktuellerIndex ? " is-jetzt" : ""
                }`}
                data-testid="ar-leiste-exkurs"
                href={config.exkurs.url}
                target="_blank"
                rel="noopener noreferrer"
                title={`${config.exkurs.url} — neuer Tab, die Sitzung bleibt hier stehen`}
              >
                <span aria-hidden="true">↗</span> {config.exkurs.label}
              </a>
            )}
          </Fragment>
        ))}
      </div>

      <div className="ar-leiste-mitte">
        <span className="ar-leiste-zaehler" data-testid="ar-leiste-zaehler">
          Schritt {aktuellerIndex + 1} von {SCHRITTE.length}
        </span>
        <span className="ar-leiste-lage" data-testid="ar-leiste-lage">
          {lage}
        </span>
        {timerRunning && typeof timerEndsAt === "number" && (
          <span className="ar-leiste-timer">
            <Timer deadline={timerEndsAt} size="inline" />
          </span>
        )}
        {timerPausiert && (
          <span className="ar-leiste-timer is-pausiert">
            {mmss(timerPausedRemainingSec ?? 0)} pausiert
          </span>
        )}
      </div>

      <div className="ar-leiste-aktionen">
        {(timerRunning || timerPausiert) && (
          <>
            <button
              type="button"
              className="ar-leiste-klein"
              onClick={() => void timerAktion(timerPausiert ? "resume" : "pause")}
              disabled={busy}
              data-testid="ar-leiste-pause"
              title={timerPausiert ? "Weiterlaufen lassen" : "Uhr anhalten"}
            >
              {timerPausiert ? "Weiter" : "Pause"}
            </button>
            <button
              type="button"
              className="ar-leiste-klein"
              onClick={() => void timerAktion("plus")}
              disabled={busy}
              data-testid="ar-leiste-plus"
              title="Zwei Minuten dazugeben"
            >
              +2
            </button>
            <button
              type="button"
              className="ar-leiste-klein"
              onClick={() => void timerAktion("stop")}
              disabled={busy}
              data-testid="ar-leiste-stop"
              title="Zeit vorzeitig beenden — das Prompten bleibt offen"
            >
              Beenden
            </button>
          </>
        )}

        {zurueck && (
          <button
            type="button"
            className="ar-leiste-klein"
            onClick={zurueck.run}
            disabled={busy}
            data-testid="ar-leiste-zurueck"
            title={`Zurück zu: ${zurueck.label}`}
          >
            ← Zurück
          </button>
        )}

        {weiter && (
          <button
            type="button"
            className="ar-leiste-weiter"
            onClick={weiter.run}
            disabled={busy}
            data-testid="ar-leiste-weiter"
            title={readOnly ? READ_ONLY_TITLE : undefined}
          >
            {busy ? "…" : `Weiter: ${weiter.label} →`}
          </button>
        )}
      </div>

      {fehler && (
        <p className="ar-leiste-fehler" role="alert">
          {fehler}
        </p>
      )}
    </div>
  );
}
